import React, {useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, ml, mt, onDesktop, position, underline, w100} from "@pg-design/helpers-css";
import {BrandMapPinIcon, PercentIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {useABTest} from "@pg-mono/ab-testing";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {abTestingInstance} from "../../../ab_testing/ab_testing_instance";
import {OngoingABTests} from "../../../ab_testing/ongoing_ab_tests";
import {IRPStore} from "../../../app/rp_reducer";
import {BoosterDetail} from "../../../atoms/booster/BoosterDetail";
import {DetailViewHeader} from "../../../atoms/DetailViewHeader";
import {HeaderCta} from "../../../real_estate/components/HeaderCta";
import {SubscribeButton} from "../../../real_estate/components/SubscribeButton";
import {Country} from "../../../region/types/Country";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {isOfferArchived} from "../../utils/is_offer_archived";
import {isOfferFavouriteButtonAvailable} from "../../utils/is_offer_favourite_button_available";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {DetailHeaderDropdownMenu} from "../DetailHeaderDropdownMenu";
import {OfferDetailHeaderPriceButton} from "./OfferDetailHeaderPriceButton";

interface IProps {
    offer: IOfferDetail;
    onLocationClick: () => void;
}

const boosterABTestProps = abTestingInstance.getABTestContainerProps(OngoingABTests.BOOSTER);

export const OfferDetailHeader = (props: IProps) => {
    const {offer} = props;
    const {vendor} = props.offer;

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const boosterTestProps = useABTest(boosterABTestProps);
    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: vendor.slug, vendorId: vendor.id});
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isForSale = isOfferForSale(offer);
    const isPlanned = offer.configuration.display_type === OfferDisplayType.PLANNED;
    const isPreSale = !!offer.configuration.pre_sale;
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";
    const isArchived = isOfferArchived(offer);

    const isPriceButtonAvailable = isOfferForSale(offer);
    const isFavouriteButtonAvailable = isOfferFavouriteButtonAvailable(offer);

    const boosterStyle = isDropdownOpen ? boosterMoved : boosterPosition;

    const handleDropdownToggle = () => {
        setIsDropdownOpen((prevState) => !prevState);
    };

    return (
        <div css={wrapper}>
            <DetailViewHeader
                title={
                    <>
                        {offer.name} {props.offer.region.country === Country.SPAIN && ` - ${getOfferTypeNamePlural(props.offer.type as OfferType)} Hiszpania`}
                        <br />
                        <Text as="span" variant="info_txt_1" css={linkStyle}>
                            <Link underline href={vendorLink} size="inherit" color={theme.colors.gray["700"]}>
                                {" "}
                                {vendor.name}
                            </Link>
                            {` - ${isApartment || isInvestmentApartment ? "apartamenty" : getOfferTypeNamePlural(offer.type)} na sprzedaż`}
                        </Text>
                    </>
                }
                cta={
                    <>
                        <HeaderCta>
                            {!isMobile && isPriceButtonAvailable && <OfferDetailHeaderPriceButton offer={offer} />}
                            {!isMobile && isFavouriteButtonAvailable && <SubscribeButton size="l" offerId={offer.id} />}
                            <DetailHeaderDropdownMenu onToggle={handleDropdownToggle} css={dropdownMenuADStyle} />
                            {boosterTestProps.canABTestBeRendered && <BoosterDetail offer={offer} css={boosterStyle} />}
                        </HeaderCta>
                    </>
                }
                badges={
                    (isArchived || isPreSale || isPlanned) && (
                        <>
                            {isArchived && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                            {isPreSale && (
                                <Badge variant="label_success">
                                    <strong>Przedsprzedaż</strong>
                                </Badge>
                            )}

                            {isPlanned && (
                                <Badge variant="label_success" icon={PercentIcon}>
                                    Inwestycja planowana
                                </Badge>
                            )}
                        </>
                    )
                }
                slogan={isForSale && offer.slogan ? offer.slogan : undefined}
                address={offer.address}
                onLocationClick={props.onLocationClick}
                addressRowButton={
                    hasStages && (
                        <Button
                            css={brandButtonStyle}
                            iconLeft={() => <BrandMapPinIcon wrapperColor={theme.colors.primary} size="2.4" wrapperSize="2.4" />}
                            variant="brand_icon"
                            size={isMobile ? "medium" : "big"}
                            onClick={props.onLocationClick}
                        >
                            <Text variant="body_copy_1" css={brandButtonTextStyle}>
                                Zobacz na mapie
                            </Text>
                        </Button>
                    )
                }
                isSpain={props.offer.region.country === Country.SPAIN}
                offerType={props.offer.type}
            />
        </div>
    );
};

const boosterMoved = css`
    transform: translate(0, ${calculateRemSize(16)});
`;

const boosterPosition = css`
    margin-top: ${calculateRemSize(4)};
`;

const wrapper = (theme: Theme) => css`
    ${display("flex")};
    ${w100};
    ${position("relative")};

    @media (min-width: ${theme.breakpoints.md}) {
        flex-wrap: nowrap;
    }
`;

const brandButtonStyle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        padding: 0;
        height: initial;
    }
`;

const brandButtonTextStyle = css`
    text-transform: none;
    ${underline};
    ${ml(0.5)};
`;

const linkStyle = (theme: Theme) => css`
    color: ${theme.colors.gray["700"]};
    display: inline-block;
`;

const dropdownMenuADStyle = css`
    ${onDesktop(css`
        ${position("relative")};
        align-self: flex-start;
        ${mt(1)};
    `)}
`;
import React, {useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, ml, mt, onDesktop, position, underline, w100} from "@pg-design/helpers-css";
import {BrandMapPinIcon, PercentIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {useABTest} from "@pg-mono/ab-testing";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {abTestingInstance} from "../../../ab_testing/ab_testing_instance";
import {OngoingABTests} from "../../../ab_testing/ongoing_ab_tests";
import {IRPStore} from "../../../app/rp_reducer";
import {BoosterDetail} from "../../../atoms/booster/BoosterDetail";
import {DetailViewHeader} from "../../../atoms/DetailViewHeader";
import {HeaderCta} from "../../../real_estate/components/HeaderCta";
import {SubscribeButton} from "../../../real_estate/components/SubscribeButton";
import {Country} from "../../../region/types/Country";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {isOfferArchived} from "../../utils/is_offer_archived";
import {isOfferFavouriteButtonAvailable} from "../../utils/is_offer_favourite_button_available";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {DetailHeaderDropdownMenu} from "../DetailHeaderDropdownMenu";
import {OfferDetailHeaderPriceButton} from "./OfferDetailHeaderPriceButton";

interface IProps {
    offer: IOfferDetail;
    onLocationClick: () => void;
}

const boosterABTestProps = abTestingInstance.getABTestContainerProps(OngoingABTests.BOOSTER);

export const OfferDetailHeader = (props: IProps) => {
    const {offer} = props;
    const {vendor} = props.offer;

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const boosterTestProps = useABTest(boosterABTestProps);
    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: vendor.slug, vendorId: vendor.id});
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isForSale = isOfferForSale(offer);
    const isPlanned = offer.configuration.display_type === OfferDisplayType.PLANNED;
    const isPreSale = !!offer.configuration.pre_sale;
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";
    const isArchived = isOfferArchived(offer);

    const isPriceButtonAvailable = isOfferForSale(offer);
    const isFavouriteButtonAvailable = isOfferFavouriteButtonAvailable(offer);

    const boosterStyle = isDropdownOpen ? boosterMoved : boosterPosition;

    const handleDropdownToggle = () => {
        setIsDropdownOpen((prevState) => !prevState);
    };

    return (
        <div css={wrapper}>
            <DetailViewHeader
                title={
                    <>
                        {offer.name} {props.offer.region.country === Country.SPAIN && ` - ${getOfferTypeNamePlural(props.offer.type as OfferType)} Hiszpania`}
                        <br />
                        <Text as="span" variant="info_txt_1" css={linkStyle}>
                            <Link underline href={vendorLink} size="inherit" color={theme.colors.gray["700"]}>
                                {" "}
                                {vendor.name}
                            </Link>
                            {` - ${isApartment || isInvestmentApartment ? "apartamenty" : getOfferTypeNamePlural(offer.type)} na sprzedaż`}
                        </Text>
                    </>
                }
                cta={
                    <>
                        <HeaderCta>
                            {!isMobile && isPriceButtonAvailable && <OfferDetailHeaderPriceButton offer={offer} />}
                            {!isMobile && isFavouriteButtonAvailable && <SubscribeButton size="l" offerId={offer.id} />}
                            <DetailHeaderDropdownMenu onToggle={handleDropdownToggle} css={dropdownMenuADStyle} />
                            {boosterTestProps.canABTestBeRendered && <BoosterDetail offer={offer} css={boosterStyle} />}
                        </HeaderCta>
                    </>
                }
                badges={
                    (isArchived || isPreSale || isPlanned) && (
                        <>
                            {isArchived && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                            {isPreSale && (
                                <Badge variant="label_success">
                                    <strong>Przedsprzedaż</strong>
                                </Badge>
                            )}

                            {isPlanned && (
                                <Badge variant="label_success" icon={PercentIcon}>
                                    Inwestycja planowana
                                </Badge>
                            )}
                        </>
                    )
                }
                slogan={isForSale && offer.slogan ? offer.slogan : undefined}
                address={offer.address}
                onLocationClick={props.onLocationClick}
                addressRowButton={
                    hasStages && (
                        <Button
                            css={brandButtonStyle}
                            iconLeft={() => <BrandMapPinIcon wrapperColor={theme.colors.primary} size="2.4" wrapperSize="2.4" />}
                            variant="brand_icon"
                            size={isMobile ? "medium" : "big"}
                            onClick={props.onLocationClick}
                        >
                            <Text variant="body_copy_1" css={brandButtonTextStyle}>
                                Zobacz na mapie
                            </Text>
                        </Button>
                    )
                }
                isSpain={props.offer.region.country === Country.SPAIN}
                offerType={props.offer.type}
            />
        </div>
    );
};

const boosterMoved = css`
    transform: translate(0, ${calculateRemSize(16)});
`;

const boosterPosition = css`
    margin-top: ${calculateRemSize(4)};
`;

const wrapper = (theme: Theme) => css`
    ${display("flex")};
    ${w100};
    ${position("relative")};

    @media (min-width: ${theme.breakpoints.md}) {
        flex-wrap: nowrap;
    }
`;

const brandButtonStyle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        padding: 0;
        height: initial;
    }
`;

const brandButtonTextStyle = css`
    text-transform: none;
    ${underline};
    ${ml(0.5)};
`;

const linkStyle = (theme: Theme) => css`
    color: ${theme.colors.gray["700"]};
    display: inline-block;
`;

const dropdownMenuADStyle = css`
    ${onDesktop(css`
        ${position("relative")};
        align-self: flex-start;
        ${mt(1)};
    `)}
`;
